import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { TileEmpty } from '../TileWrapper';
import { MessageSquare, CheckCircle } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { cn } from '@/lib/utils';

// Mock data for simulation
const MOCK_DATA = {
  conversations: [
    {
      conversationId: '1',
      latestMessage: {
        content: 'Can you check the risk assessment for the new site?',
        created_date: new Date(Date.now() - 1800000).toISOString(),
        sender: { id: '1', first_name: 'Mike', last_name: 'Williams', avatar_url: null },
      },
      count: 3,
    },
    {
      conversationId: '2',
      latestMessage: {
        content: 'The training session has been rescheduled to Monday',
        created_date: new Date(Date.now() - 7200000).toISOString(),
        sender: { id: '2', first_name: 'Sarah', last_name: 'Johnson', avatar_url: null },
      },
      count: 1,
    },
    {
      conversationId: '3',
      latestMessage: {
        content: 'Please review the attached COSHH form',
        created_date: new Date(Date.now() - 14400000).toISOString(),
        sender: { id: '3', first_name: 'David', last_name: 'Lee', avatar_url: null },
      },
      count: 2,
    },
  ],
  totalUnread: 6,
};

export function UnreadMessagesTile({ filter, size, useMockData }) {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const [data, setData] = useState({ conversations: [], totalUnread: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (useMockData) {
      setData({
        conversations: MOCK_DATA.conversations.slice(0, size === 'medium' ? 3 : 2),
        totalUnread: MOCK_DATA.totalUnread,
      });
      setLoading(false);
      return;
    }

    if (!userProfile?.id) return;

    const loadData = async () => {
      setLoading(true);
      try {
        // Get conversations user is part of
        const { data: memberships, error: memberError } = await supabase
          .from('conversation_members')
          .select('conversation_id')
          .eq('user_id', userProfile.id);

        if (memberError) throw memberError;

        const conversationIds = memberships?.map(m => m.conversation_id) || [];

        if (conversationIds.length === 0) {
          setData({ conversations: [], totalUnread: 0 });
          setLoading(false);
          return;
        }

        // Get unread messages per conversation
        const { data: unreadCounts, error: countError } = await supabase
          .from('messages')
          .select('conversation_id, id, content, created_date, sender:user_profiles!sender_id(id, first_name, last_name, avatar_url)')
          .in('conversation_id', conversationIds)
          .neq('sender_id', userProfile.id)
          .order('created_date', { ascending: false });

        if (countError) throw countError;

        // Get read receipts
        const { data: reads } = await supabase
          .from('message_reads')
          .select('message_id')
          .eq('user_id', userProfile.id);

        const readMessageIds = new Set(reads?.map(r => r.message_id) || []);

        // Filter to unread and group by conversation
        const unreadByConvo = {};
        let totalUnread = 0;

        (unreadCounts || []).forEach(msg => {
          if (!readMessageIds.has(msg.id)) {
            totalUnread++;
            if (!unreadByConvo[msg.conversation_id]) {
              unreadByConvo[msg.conversation_id] = {
                conversationId: msg.conversation_id,
                latestMessage: msg,
                count: 0,
              };
            }
            unreadByConvo[msg.conversation_id].count++;
          }
        });

        const conversations = Object.values(unreadByConvo)
          .sort((a, b) => new Date(b.latestMessage.created_date) - new Date(a.latestMessage.created_date))
          .slice(0, size === 'medium' ? 3 : 2);

        setData({ conversations, totalUnread });
      } catch (err) {
        console.error('Failed to load unread messages:', err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [userProfile?.id, size, useMockData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-pulse text-center">
          <div className="w-12 h-12 rounded-full bg-gray-200 mx-auto mb-2" />
          <div className="h-4 w-16 bg-gray-200 rounded mx-auto" />
        </div>
      </div>
    );
  }

  if (data.totalUnread === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center">
        <div className="bg-green-100 p-3 rounded-full mb-2">
          <CheckCircle className="h-6 w-6 text-green-600" />
        </div>
        <p className="text-sm font-medium text-green-700">All Caught Up</p>
        <p className="text-xs text-gray-500">No unread messages</p>
      </div>
    );
  }

  if (size === 'small') {
    return (
      <div
        onClick={() => navigate('/chat')}
        className="flex flex-col items-center justify-center h-full text-center cursor-pointer hover:bg-gray-50 rounded-lg transition-colors"
      >
        <div className="bg-blue-100 p-3 rounded-full mb-2 relative">
          <MessageSquare className="h-6 w-6 text-blue-600" />
          <Badge className="absolute -top-1 -right-1 h-5 min-w-5 flex items-center justify-center p-0 text-xs">
            {data.totalUnread}
          </Badge>
        </div>
        <p className="text-sm text-gray-600">Unread</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between mb-2">
        <Badge>{data.totalUnread} unread</Badge>
      </div>

      {data.conversations.map((convo) => {
        const sender = convo.latestMessage.sender;

        return (
          <div
            key={convo.conversationId}
            onClick={() => navigate('/chat')}
            className="flex items-start gap-3 p-2 rounded-lg bg-blue-50 hover:bg-blue-100 cursor-pointer transition-colors"
          >
            <Avatar className="h-8 w-8">
              <AvatarImage src={sender?.avatar_url} />
              <AvatarFallback className="text-xs">
                {sender?.first_name?.[0]}{sender?.last_name?.[0]}
              </AvatarFallback>
            </Avatar>

            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium truncate">
                  {sender?.first_name} {sender?.last_name}
                </p>
                <Badge variant="secondary" className="text-xs ml-2">
                  {convo.count}
                </Badge>
              </div>
              <p className="text-xs text-gray-600 truncate">
                {convo.latestMessage.content}
              </p>
              <p className="text-xs text-gray-400">
                {formatDistanceToNow(new Date(convo.latestMessage.created_date), { addSuffix: true })}
              </p>
            </div>
          </div>
        );
      })}

      <button
        onClick={() => navigate('/chat')}
        className="w-full text-center text-sm text-blue-600 hover:text-blue-700 py-2"
      >
        Open Chat
      </button>
    </div>
  );
}
